// src/components/DeletedReservationsModal.jsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  X, 
  Archive, 
  Loader2, 
  Calendar, 
  User, 
  Mail, 
  Phone, 
  Clock, 
  Users, 
  MessageSquare,
  Undo2,
  Trash2,
  Search,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';
import { Button } from './ui/button';
import { format, parseISO } from 'date-fns';

export default function DeletedReservationsModal({ 
  deletedReservations, 
  loading, 
  onClose, 
  onRestore, 
  onPermanentDelete,
  formatDate 
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Filter deleted reservations
  const filteredReservations = deletedReservations.filter(res => {
    const searchLower = searchTerm.toLowerCase();
    return (
      res.customer_name?.toLowerCase().includes(searchLower) ||
      res.email?.toLowerCase().includes(searchLower) ||
      res.phone?.toLowerCase().includes(searchLower) ||
      res.deletion_reason?.toLowerCase().includes(searchLower)
    );
  });

  // Pagination
  const totalPages = Math.ceil(filteredReservations.length / itemsPerPage);
  const paginatedReservations = filteredReservations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset page when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Format time
  const formatTime = (timeString) => {
    return timeString || 'N/A';
  };

  // Get status color
  const getStatusColor = (status) => {
    switch(status) {
      case 'approved': return 'bg-emerald-100 text-emerald-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      case 'pending': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="sticky top-0 bg-gradient-to-r from-[#8B2635] to-[#B23B4A] text-white p-3 sm:p-6 z-10">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 sm:gap-3">
              <Archive className="w-5 h-5 sm:w-6 sm:h-6" />
              <div>
                <h2 className="text-lg sm:text-xl font-bold">Deleted Reservations</h2>
                <p className="text-xs sm:text-sm text-white/80 mt-0.5 sm:mt-1">
                  Total Archived: {deletedReservations.length}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="p-1.5 sm:p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Modal Content */}
        <div className="p-3 sm:p-6 overflow-y-auto max-h-[calc(95vh-80px)] sm:max-h-[calc(90vh-100px)]">
          {/* Search Bar */}
          <div className="mb-4 sm:mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#6B6B6B]" />
              <input
                type="text"
                placeholder="Search by name, email, phone, or deletion reason..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B2635]/40 focus:border-[#8B2635]"
              />
            </div>
          </div>

          {/* Stats Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
            <div className="bg-[#F9F9F9] p-3 sm:p-4 rounded-lg border border-[#E5E5E5]">
              <p className="text-xs text-[#6B6B6B]">Total Deleted</p>
              <p className="text-lg sm:text-2xl font-bold text-[#2D2D2D]">{deletedReservations.length}</p>
            </div>
            <div className="bg-[#F9F9F9] p-3 sm:p-4 rounded-lg border border-[#E5E5E5]">
              <p className="text-xs text-[#6B6B6B]">This Month</p>
              <p className="text-lg sm:text-2xl font-bold text-[#8B2635]">
                {deletedReservations.filter(r => {
                  if (!r.deleted_at) return false;
                  const deleted = new Date(r.deleted_at);
                  const now = new Date();
                  return deleted.getMonth() === now.getMonth() && deleted.getFullYear() === now.getFullYear();
                }).length}
              </p>
            </div>
            <div className="bg-[#F9F9F9] p-3 sm:p-4 rounded-lg border border-[#E5E5E5]">
              <p className="text-xs text-[#6B6B6B]">Filtered</p>
              <p className="text-lg sm:text-2xl font-bold text-[#2D2D2D]">{filteredReservations.length}</p>
            </div>
            <div className="bg-[#F9F9F9] p-3 sm:p-4 rounded-lg border border-[#E5E5E5]">
              <p className="text-xs text-[#6B6B6B]">Pages</p>
              <p className="text-lg sm:text-2xl font-bold text-[#8B2635]">{totalPages}</p>
            </div>
          </div>

          {/* Deleted Reservations Table - Desktop */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#8B2635]" />
            </div>
          ) : filteredReservations.length > 0 ? (
            <>
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-[#E5E5E5] bg-[#F9F9F9]">
                      <th className="text-left py-3 px-4 text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">Customer</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">Contact</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">Reservation</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">Deleted At</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">Deletion Reason</th>
                      <th className="text-right py-3 px-4 text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedReservations.map((res) => (
                      <tr key={res.id} className="border-b border-[#E5E5E5] hover:bg-[#F9F9F9] transition-colors">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-[#8B2635]/10 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-[#8B2635]" />
                            </div>
                            <span className="font-medium text-[#2D2D2D]">{res.customer_name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="space-y-1">
                            <div className="flex items-center gap-1 text-xs">
                              <Mail className="w-3 h-3 text-[#6B6B6B]" />
                              <span className="text-[#2D2D2D]">{res.email}</span>
                            </div>
                            <div className="flex items-center gap-1 text-xs">
                              <Phone className="w-3 h-3 text-[#6B6B6B]" />
                              <span className="text-[#2D2D2D]">{res.phone}</span>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="space-y-1">
                            <div className="flex items-center gap-1 text-xs">
                              <Calendar className="w-3 h-3 text-[#6B6B6B]" />
                              <span>{formatDate(res.date)} at {res.time}</span>
                            </div>
                            <div className="flex items-center gap-1 text-xs">
                              <Users className="w-3 h-3 text-[#6B6B6B]" />
                              <span>{res.guests} guests</span>
                              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${getStatusColor(res.status)}`}>
                                {res.status}
                              </span>
                            </div>
                            {res.table_number && (
                              <div className="text-xs text-[#8B2635]">
                                Table #{res.table_number}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="space-y-1">
                            <div className="flex items-center gap-1 text-xs">
                              <Clock className="w-3 h-3 text-[#6B6B6B]" />
                              <span>{format(parseISO(res.deleted_at), 'dd/MM/yyyy')}</span>
                            </div>
                            <div className="text-xs text-[#6B6B6B]">
                              {format(parseISO(res.deleted_at), 'HH:mm')}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-start gap-1 max-w-xs">
                            <MessageSquare className="w-3 h-3 text-[#6B6B6B] mt-0.5 flex-shrink-0" />
                            <span className="text-xs text-[#2D2D2D] break-words">
                              {res.deletion_reason}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => onRestore(res)}
                              className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300"
                              title="Restore reservation"
                            >
                              <Undo2 className="w-3 h-3 sm:w-4 sm:h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => onPermanentDelete(res)}
                              className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                              title="Permanently delete"
                            >
                              <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Deleted Reservations Cards - Mobile */}
              <div className="sm:hidden space-y-3">
                {paginatedReservations.map((res) => (
                  <div key={res.id} className="bg-white border border-[#E5E5E5] rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-[#8B2635]/10 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-[#8B2635]" />
                        </div>
                        <div>
                          <p className="font-medium text-[#2D2D2D]">{res.customer_name}</p>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(res.status)}`}>
                            {res.status}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-[#6B6B6B]" />
                        <span className="text-[#2D2D2D]">{res.email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-[#6B6B6B]" />
                        <span className="text-[#2D2D2D]">{res.phone}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-[#6B6B6B]" />
                        <span>{formatDate(res.date)} at {res.time}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-[#6B6B6B]" />
                        <span>{res.guests} guests</span>
                      </div>
                      {res.table_number && (
                        <div className="text-sm text-[#8B2635] font-medium">
                          Table #{res.table_number}
                        </div>
                      )}
                      <div className="flex items-start gap-2 pt-2 border-t border-[#E5E5E5]">
                        <MessageSquare className="w-4 h-4 text-[#8B2635] mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-medium text-[#6B6B6B]">Deletion Reason:</p>
                          <p className="text-sm text-[#2D2D2D]">{res.deletion_reason}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-[#6B6B6B] pt-2">
                        <Clock className="w-3 h-3" />
                        <span>Deleted: {format(parseISO(res.deleted_at), 'dd/MM/yyyy HH:mm')}</span>
                      </div>
                    </div>

                    <div className="flex gap-2 mt-4 pt-3 border-t border-[#E5E5E5]">
                      <Button
                        size="sm"
                        onClick={() => onRestore(res)}
                        className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white text-xs"
                      >
                        <Undo2 className="w-3 h-3 mr-1" />
                        Restore
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onPermanentDelete(res)}
                        className="flex-1 border-red-200 text-red-600 hover:bg-red-50 text-xs"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 sm:mt-6 pt-4 border-t border-[#E5E5E5]">
                  <p className="text-xs sm:text-sm text-[#6B6B6B]">
                    Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredReservations.length)} of {filteredReservations.length} records
                  </p>
                  <div className="flex items-center gap-1 sm:gap-2">
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="p-1 sm:p-2 border border-[#E5E5E5] rounded-lg hover:bg-[#F5F5F5] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronsLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="p-1 sm:p-2 border border-[#E5E5E5] rounded-lg hover:bg-[#F5F5F5] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="px-2 sm:px-3 py-1 sm:py-2 text-xs sm:text-sm">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="p-1 sm:p-2 border border-[#E5E5E5] rounded-lg hover:bg-[#F5F5F5] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="p-1 sm:p-2 border border-[#E5E5E5] rounded-lg hover:bg-[#F5F5F5] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronsRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 sm:py-16">
              <Archive className="w-16 h-16 sm:w-20 sm:h-20 text-[#CCCCCC] mx-auto mb-3 sm:mb-4" />
              <p className="text-base sm:text-lg font-medium text-[#2D2D2D]">No deleted reservations found</p>
              <p className="text-xs sm:text-sm text-[#6B6B6B] mt-1">
                {searchTerm ? 'Try adjusting your search' : 'Deleted reservations will appear here'}
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}